import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Pagination,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Card,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Building2Icon from "@mui/icons-material/Business";
import MapPinIcon from "@mui/icons-material/LocationOn";
import LayerIcon from "@mui/icons-material/Layers";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import CustomCircularP from "../../widgets/CustomCircularP";
import ExportExhibitorsButton from "./ExportExhibitorsButton";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";

const Exhibitors = () => {
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(1);
  const [sq, setSq] = useState("");
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-exhibitors?page=${page}&sq=${sq}&r=${reload}`
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const handleSearch = (e) => {
    const vl = e.target.value;
    setSq(vl);
    setPage(1);
  };

  const debouncedHandleSearch = useCallback(debounce(handleSearch, 400), []);

  const pageChange = (e, p) => {
    setPage(p);
  };

  // Helper functions
  const getFieldValue = (field) => field?.value ?? "-";

  const getContactName = (user) => {
    const salutation = user.contactSalutation?.value || "";
    const firstName = user.contactFirstName?.value || "";
    const lastName = user.contactLastName?.value || "";
    const fullName = `${salutation} ${firstName} ${lastName}`.trim();
    return fullName || "-";
  };

  const getLocation = (user) => {
    const country = user.country?.value?.name || "";
    const state = user.state?.value?.name || "";
    const city = user.city?.value?.name || "";
    const parts = [city, state, country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const getFloorAllocation = (user) => {
    const rawHall = (user.hallNumber?.value || "").trim();
    const rawStand = (user.standNumber?.value || "").trim();

    const hallFormatted = rawHall
      ? rawHall.toLowerCase().startsWith("hall")
        ? rawHall
        : `Hall ${rawHall}`
      : "";

    const standFormatted = rawStand
      ? rawStand.toLowerCase().startsWith("stand")
        ? rawStand
        : `Stand ${rawStand}`
      : "";

    if (hallFormatted && standFormatted) {
      return `${hallFormatted} • ${standFormatted}`;
    } else if (hallFormatted || standFormatted) {
      return hallFormatted || standFormatted;
    }
    return "-";
  };

  const getIndustrySectors = (user) => {
    const sectors = user.industrySector?.value;
    if (Array.isArray(sectors) && sectors.length > 0) {
      return sectors;
    }
    return [];
  };

  const totalExhibitors = data?.totalItems || 0;
  const totalPages = Math.ceil(totalExhibitors / 30) || 1;

  return (
    <Box sx={{ pb: 6 }}>
      <CustomCircularP show={isLoading} />

      {/* Top Action Header Bar */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <BackBtn />
        <Box flex={1}>
          <Typography
            variant="h4"
            fontWeight="800"
            color="#1E293B"
            sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" }, letterSpacing: "-0.02em" }}
          >
            Exhibitors Directory
          </Typography>
          <Typography variant="body2" color="#64748B" fontWeight="500">
            Manage & monitor all registered event exhibitors ({totalExhibitors.toLocaleString()} total records)
          </Typography>
        </Box>
      </Stack>

      {/* Metric Summary Cards Bar */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#EEF4FF",
                color: "#2563EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2Icon />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748B" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
                Total Exhibitors
              </Typography>
              <Typography variant="h5" fontWeight="800" color="#1E293B" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                {totalExhibitors.toLocaleString()}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#F0FDF4",
                color: "#16A34A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LayerIcon />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748B" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
                Current Page
              </Typography>
              <Typography variant="h5" fontWeight="800" color="#1E293B" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                Page {page} of {totalPages}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#F3E8FF",
                color: "#7C3AED",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MapPinIcon />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748B" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
                Active Event
              </Typography>
              <Typography variant="h5" fontWeight="800" color="#1E293B" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                PMTX 2026
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 4px 20px rgba(15, 23, 42, 0.04)",
          overflow: "hidden",
        }}
      >
        {/* Search & Actions Toolbar Header */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid #E2E8F0",
            backgroundColor: "#FAFBFD",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Search Box */}
          <TextField
            placeholder="Search by company name or contact..."
            size="small"
            onChange={debouncedHandleSearch}
            sx={{
              width: { xs: "100%", md: 380 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#FFFFFF",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Action Buttons */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
            <Button
              onClick={() => navigate("/update-exhibitor-excel")}
              variant="outlined"
              startIcon={<FileUploadIcon />}
              sx={{
                borderRadius: "10px",
                borderColor: "#CBD5E1",
                color: "#475569",
                fontWeight: 600,
                textTransform: "none",
                py: 0.8,
                px: 2,
                "&:hover": { backgroundColor: "#F1F5F9", borderColor: "#94A3B8" },
              }}
            >
              Excel Import
            </Button>

            <ExportExhibitorsButton />

            <Button
              onClick={() => navigate("/add-exhibitor")}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: "10px",
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                fontWeight: 600,
                textTransform: "none",
                py: 0.8,
                px: 2.5,
                boxShadow: "0px 4px 12px rgba(37, 99, 235, 0.25)",
                "&:hover": { backgroundColor: "#1D4ED8" },
              }}
            >
              Add Exhibitor
            </Button>
          </Stack>
        </Box>

        {/* Exhibitors Data Table */}
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 1250, tableLayout: "auto" }} size="small" aria-label="exhibitors table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "24%", minWidth: 260 }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "15%", minWidth: 160 }}>Contact Person</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "20%", minWidth: 220 }}>Phone & Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "16%", minWidth: 180 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "12%", minWidth: 150 }}>Floor / Stand</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "13%", minWidth: 150 }}>Industry Sector</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: 90, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.data || data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="#64748B" fontWeight="600">
                      No exhibitors found matching your search.
                    </Typography>
                    <Typography variant="caption" color="#94A3B8">
                      Try adjusting your search terms or add a new exhibitor.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.data.map((user, index) => {
                  const companyName = getFieldValue(user.companyName);
                  const contactName = getContactName(user);
                  const email = getFieldValue(user.contactEmail);
                  const phone = getFieldValue(user.phone);
                  const location = getLocation(user);
                  const floorAlloc = getFloorAllocation(user);
                  const sectors = getIndustrySectors(user);

                  return (
                    <TableRow
                      key={user._id || index}
                      sx={{
                        "&:hover": { backgroundColor: "#F8FAFC" },
                        transition: "background-color 0.15s ease-in-out",
                      }}
                    >
                      {/* Company Name & Avatar */}
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar
                            src={user.profile_image || ""}
                            alt={companyName}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#EEF4FF",
                              color: "#2563EB",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              border: "1px solid #DBEAFE",
                            }}
                          >
                            {companyName ? companyName.charAt(0).toUpperCase() : "C"}
                          </Avatar>
                          <Box>
                            {user._id ? (
                              <Link
                                to={`/exhibitor/${user._id}`}
                                style={{ textDecoration: "none", color: "#2563EB", fontWeight: 700 }}
                              >
                                <Typography variant="subtitle2" fontWeight="700" color="#2563EB" sx={{ fontSize: "0.85rem", "&:hover": { textDecoration: "underline" } }}>
                                  {companyName}
                                </Typography>
                              </Link>
                            ) : (
                              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.85rem" }}>
                                {companyName}
                              </Typography>
                            )}
                            {user.fascia?.value && (
                              <Typography variant="caption" color="#64748B" display="block" sx={{ fontSize: "0.725rem" }}>
                                Fascia: {user.fascia.value}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Contact Person */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" fontWeight="600" color="#1E293B" sx={{ fontSize: "0.825rem" }}>
                          {contactName}
                        </Typography>
                        {user.pin?.value && (
                          <Typography variant="caption" color="#94A3B8" display="block" sx={{ fontSize: "0.725rem" }}>
                            PIN: {user.pin.value}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Phone & Email */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="#334155" fontWeight="500" sx={{ fontSize: "0.825rem" }}>
                          {email}
                        </Typography>
                        <Typography variant="caption" color="#64748B" display="block" sx={{ fontSize: "0.725rem" }}>
                          {phone}
                        </Typography>
                      </TableCell>

                      {/* Location */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="#475569" fontWeight="500" sx={{ fontSize: "0.825rem" }}>
                          {location}
                        </Typography>
                      </TableCell>

                      {/* Floor / Stand Badge */}
                      <TableCell sx={{ py: 1 }}>
                        {floorAlloc !== "-" ? (
                          <Chip
                            label={floorAlloc}
                            size="small"
                            sx={{
                              backgroundColor: "#EEF4FF",
                              color: "#2563EB",
                              fontWeight: 700,
                              fontSize: "0.725rem",
                              borderRadius: "6px",
                              border: "1px solid #C7D9FE",
                              height: 22,
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="#94A3B8" sx={{ fontSize: "0.825rem" }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Industry Sector Tags */}
                      <TableCell sx={{ py: 1 }}>
                        {sectors.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {sectors.map((sec, i) => (
                              <Chip
                                key={i}
                                label={sec}
                                size="small"
                                sx={{
                                  backgroundColor: "#F1F5F9",
                                  color: "#475569",
                                  fontWeight: 600,
                                  fontSize: "0.675rem",
                                  borderRadius: "4px",
                                  height: 20,
                                }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="#94A3B8" sx={{ fontSize: "0.825rem" }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ py: 1, textAlign: "right" }}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {user._id && (
                            <>
                              <Tooltip title="View Exhibitor Details">
                                <Link to={`/exhibitor/${user._id}`}>
                                  <IconButton size="small" sx={{ color: "#64748B", p: 0.5, "&:hover": { backgroundColor: "#F1F5F9" } }}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Link>
                              </Tooltip>

                              <Tooltip title="Edit Exhibitor">
                                <Link to={`/edit-exhibitor-form/${user._id}`}>
                                  <IconButton size="small" sx={{ color: "#2563EB", p: 0.5, "&:hover": { backgroundColor: "#EEF4FF" } }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Link>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Footer Bar with Pagination & Bulk Delete */}
        <Box
          sx={{
            p: 2.5,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: "#FAFBFD",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="#64748B" fontWeight="500">
            Showing page {page} of {totalPages} ({totalExhibitors.toLocaleString()} exhibitors)
          </Typography>

          {data && (
            <Pagination
              page={page}
              onChange={pageChange}
              count={totalPages}
              color="primary"
              shape="rounded"
            />
          )}

          {data && data?.data?.length > 0 && (
            <Box>
              <SimpleYesNoDialog
                title="Please Confirm Delete"
                message={`Are you sure you want to delete all exhibitors? This action cannot be undone.`}
                onYes={async () => {
                  setDeleting(true);
                  setShowDelete(false);
                  try {
                    const { data } = await axios.delete(
                      `${API_ENDPOINT}/admin/delete-all-exhibitors`,
                      getHeader(ADMIN)
                    );
                    toast.success(data.message);
                    setTimeout(() => {
                      document.location.reload();
                    }, 400);
                  } catch (error) {
                    toastError(error);
                  } finally {
                    setDeleting(false);
                  }
                }}
                show={showDelete}
                setShow={setShowDelete}
              />
              <LoadingButton
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setShowDelete(true)}
                loading={deleting}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
              >
                Delete All Exhibitors
              </LoadingButton>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Exhibitors;
